import React from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '@/assets/styles/colors';
import { Card } from '@/components/ui/cards';
import { IconScout } from '@/components/ui/images/IconScout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import type { SearchFilterConfig } from '@/hooks/attendance/common/useAttendanceSearchFilters';
import type { DimensionalFilterConfig } from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import type { UseAttendanceFiltersPanelResult } from '@/hooks/attendance/common/useAttendanceFiltersPanel';

import { AttendanceSearchFilters } from './AttendanceSearchFilters';
import { EmployeeDimensionalFilter } from './EmployeeDimensionalFilter';


interface AttendanceFiltersPanelProps {
  config: SearchFilterConfig;
  state: UseAttendanceFiltersPanelResult;
  dimensionalConfig?: DimensionalFilterConfig;
}

export const AttendanceFiltersPanel: React.FC<AttendanceFiltersPanelProps> = ({
  config,
  state,
  dimensionalConfig,
}) => {
  const { t: translate } = useTranslation();

  const showDimensional = dimensionalConfig != null
    && Object.values(dimensionalConfig).some(Boolean);

  return (
    <Card>
      <div className="flex flex-col gap-4 p-4">
        <AttendanceSearchFilters
          config={config}
          state={state.searchFilters}
        />

        {showDimensional && (
          <>
            <button
              type="button"
              onClick={state.dimensionalFilter.toggleExpanded}
              className="self-start flex items-center gap-1 font-medium text-sm cursor-pointer bg-transparent border-none p-0"
              style={{ color: colors.important.main }}
              aria-expanded={state.dimensionalFilter.isExpanded}
            >
              {state.dimensionalFilter.isExpanded
                ? translate('attendance:dimensionalFilter.hide')
                : translate('attendance:dimensionalFilter.showAdvancedFilters')}
              <IconScout
                name={state.dimensionalFilter.isExpanded ? 'angleUp' : 'angleDown'}
                size={16}
              />
            </button>

            {state.dimensionalFilter.isExpanded && (
              <>
                <EmployeeDimensionalFilter
                  config={dimensionalConfig}
                  state={state.dimensionalFilter}
                />
                <div className="self-end">
                  <PrimaryButton onClick={state.handleSearch}>
                    {translate('attendance:searchFilters.search')}
                  </PrimaryButton>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
